import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AutoAwesomeOutlined as AutoAwesome,
  ContentCopyOutlined as ContentCopy,
  DownloadOutlined as Download,
  Facebook,
  LanguageOutlined as Language,
  BusinessOutlined as Business,
  HomeOutlined as Home,
  RefreshOutlined as Refresh
} from '@mui/icons-material';
import api from '../api';

const AIListingGenerator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [generatedListings, setGeneratedListings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewDialog, setPreviewDialog] = useState({ open: false, content: '', platform: '' });

  // Fetch properties on component mount
  useEffect(() => {
    fetchProperties();
    fetchPlatforms();
  }, []);

  // Fetch units when property changes
  useEffect(() => {
    if (selectedProperty) {
      fetchUnits(selectedProperty);
    } else {
      setUnits([]);
      setSelectedUnit('');
    }
  }, [selectedProperty, properties]);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties');
      // Properties endpoint returns array directly
      if (Array.isArray(response.data)) {
        setProperties(response.data);
      } else {
        setProperties([]);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      setError('Failed to fetch properties');
    }
  };

  const fetchUnits = async (propertyId) => {
    try {
      // Units are included in the property data, so we need to find the property
      const property = properties.find(p => p.id === propertyId);
      if (property && property.units) {
        setUnits(property.units);
      } else {
        setUnits([]);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      setError('Failed to fetch units');
    }
  };

  const fetchPlatforms = async () => {
    try {
      const response = await api.get('/api/ai-listing/platforms');
      if (response.data.success) {
        const fetchedPlatforms = response.data.data?.platforms || [];
        setPlatforms(fetchedPlatforms);
        // Set default platform if none selected and platforms are available
        if (!selectedPlatform && fetchedPlatforms.length > 0) {
          setSelectedPlatform(fetchedPlatforms[0].id || 'general');
        }
      } else {
        // Fallback to default platforms if API doesn't return them
        const fallbackPlatforms = [
          { id: 'general', name: 'General Listing' },
          { id: 'facebook', name: 'Facebook Marketplace' },
          { id: 'craigslist', name: 'Craigslist' },
          { id: 'rentfaster', name: 'RentFaster' }
        ];
        setPlatforms(fallbackPlatforms);
        if (!selectedPlatform) {
          setSelectedPlatform('general');
        }
      }
    } catch (error) {
      console.error('Error fetching platforms:', error);
      // Fallback to default platforms on error
      const fallbackPlatforms = [
        { id: 'general', name: 'General Listing' },
        { id: 'facebook', name: 'Facebook Marketplace' },
        { id: 'craigslist', name: 'Craigslist' },
        { id: 'rentfaster', name: 'RentFaster' }
      ];
      setPlatforms(fallbackPlatforms);
      if (!selectedPlatform) {
        setSelectedPlatform('general');
      }
    }
  };

  const generateListing = async (platform = selectedPlatform) => {
    if (!selectedProperty || !selectedUnit) {
      setError('Please select both a property and unit');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use a longer timeout for AI generation (60 seconds)
      const response = await api.post('/api/ai-listing/generate', {
        propertyId: selectedProperty,
        unitId: selectedUnit,
        platform
      }, {
        timeout: 60000 // 60 seconds for AI generation
      });

      if (response.data.success) {
        setGeneratedListings(prev => ({
          ...prev,
          [platform]: response.data.data.generatedCopy
        }));
        setSuccess(`AI listing generated successfully for ${platform}!`);
      } else {
        setError(response.data.error || 'Failed to generate listing');
      }
    } catch (error) {
      console.error('Error generating listing:', error);
      setError(error.response?.data?.error || error.message || 'Failed to generate listing. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateMultiPlatformListings = async () => {
    if (!selectedProperty || !selectedUnit) {
      setError('Please select both a property and unit');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use a longer timeout for AI generation (120 seconds for multi-platform)
      const response = await api.post('/api/ai-listing/generate-multi', {
        propertyId: selectedProperty,
        unitId: selectedUnit
      }, {
        timeout: 120000 // 120 seconds (2 minutes) for multi-platform AI generation
      });

      if (response.data.success) {
        setGeneratedListings(response.data.data.listings);
        setSuccess('Multi-platform listings generated successfully!');
      } else {
        setError(response.data.error || 'Failed to generate listings');
      }
    } catch (error) {
      console.error('Error generating multi-platform listings:', error);
      setError(error.response?.data?.error || error.message || 'Failed to generate listings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      setError('Failed to copy to clipboard');
    }
  };

  const downloadListing = (content, platform) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${platform}-listing.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook': return <Facebook />;
      case 'craigslist': return <Language />;
      case 'rentfaster': return <Business />;
      default: return <Home />;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'facebook': return '#1877f2';
      case 'craigslist': return '#ff6600';
      case 'rentfaster': return '#2e7d32';
      default: return '#1976d2';
    }
  };

  const selectedPropertyData = properties.find(p => p.id === selectedProperty);
  const selectedUnitData = units.find(u => u.id === selectedUnit);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 },
          fontSize: { xs: '1.5rem', sm: '2rem' },
          mb: { xs: 1, sm: 1.5 }
        }}
      >
        <AutoAwesome color="primary" sx={{ fontSize: { xs: 24, sm: 28 } }} />
        {isMobile ? 'AI Listing' : 'AI Listing Generator'}
      </Typography>
      
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ 
          mb: { xs: 2, sm: 3 },
          fontSize: { xs: '0.875rem', sm: '1rem' }
        }}
      >
        Generate compelling property listings using AI for different platforms
      </Typography>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: { xs: 1.5, sm: 2 } }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: { xs: 1.5, sm: 2 } }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {/* Selection Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
              >
                {isMobile ? 'Selection' : 'Property & Unit Selection'}
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Property</InputLabel>
                <Select
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  label="Property"
                >
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={selectedUnit}
                  onChange={(e) => setSelectedUnit(e.target.value)}
                  label="Unit"
                  disabled={!selectedProperty}
                >
                  {units.map((unit) => {
                    // Build a meaningful display name from available fields
                    const unitName = unit.name || unit.unitNumber || unit.id || 'Unit';
                    const bedrooms = unit.bedrooms ? `${unit.bedrooms}BR` : '';
                    const bathrooms = unit.bathrooms ? `${unit.bathrooms}BA` : '';
                    const rent = unit.rent || unit.monthlyRent ? `$${unit.rent || unit.monthlyRent}` : '';
                    
                    // Build display string
                    let displayText = unitName;
                    if (bedrooms || bathrooms) {
                      displayText += ` - ${bedrooms}${bedrooms && bathrooms ? '/' : ''}${bathrooms}`;
                    }
                    if (rent) {
                      displayText += ` - ${rent}`;
                    }
                    
                    return (
                      <MenuItem key={unit.id} value={unit.id}>
                        {displayText}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  label="Platform"
                >
                  {platforms.map((platform) => (
                    <MenuItem key={platform.id} value={platform.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getPlatformIcon(platform.id)}
                        {platform.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                fullWidth
                onClick={() => generateListing()}
                disabled={!selectedProperty || !selectedUnit || isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <AutoAwesome />}
                sx={{ mb: 1 }}
              >
                {isLoading ? 'Generating...' : 'Generate Single Listing'}
              </Button>

              <Button
                variant="outlined"
                fullWidth
                onClick={generateMultiPlatformListings}
                disabled={!selectedProperty || !selectedUnit || isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <Refresh />}
              >
                {isLoading ? 'Generating...' : 'Generate All Platforms'}
              </Button>
            </CardContent>
          </Card>

          {/* Property Info */}
          {selectedPropertyData && selectedUnitData && (
            <Card sx={{ mt: { xs: 1.5, sm: 2 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
                >
                  {isMobile ? 'Property Info' : 'Selected Property Info'}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}
                >
                  <strong>Property:</strong> {selectedPropertyData.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}
                >
                  <strong>Address:</strong> {selectedPropertyData.address}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}
                >
                  <strong>Unit:</strong> {selectedUnitData.name || selectedUnitData.unitNumber || selectedUnitData.id}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}
                >
                  <strong>Rent:</strong> ${selectedUnitData.rent || selectedUnitData.monthlyRent || 'N/A'}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}
                >
                  <strong>Bedrooms:</strong> {selectedUnitData.bedrooms}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  <strong>Bathrooms:</strong> {selectedUnitData.bathrooms}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Generated Listings */}
        <Grid item xs={12} md={8}>
          {Object.keys(generatedListings).length > 0 ? (
            <Box>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
              >
                Generated Listings
              </Typography>
              
              {platforms.map((platform) => {
                const listing = generatedListings[platform.id];
                if (!listing) return null;

                return (
                  <Card key={platform.id} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', 
                        alignItems: { xs: 'flex-start', sm: 'center' }, 
                        mb: { xs: 1.5, sm: 2 },
                        gap: { xs: 1, sm: 0 }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flex: 1, minWidth: 0 }}>
                          <Box sx={{ color: getPlatformColor(platform.id) }}>
                            {getPlatformIcon(platform.id)}
                          </Box>
                          <Typography 
                            variant="h6"
                            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                          >
                            {platform.name}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 } }}>
                          <Tooltip title="Copy to clipboard">
                            <IconButton
                              onClick={() => copyToClipboard(listing)}
                              size={isMobile ? 'small' : 'medium'}
                              aria-label="Copy listing to clipboard"
                            >
                              <ContentCopy fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Download as text file">
                            <IconButton
                              onClick={() => downloadListing(listing, platform.id)}
                              size={isMobile ? 'small' : 'medium'}
                              aria-label="Download listing"
                            >
                              <Download fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Preview">
                            <IconButton
                              onClick={() => setPreviewDialog({ 
                                open: true, 
                                content: listing, 
                                platform: platform.name 
                              })}
                              size={isMobile ? 'small' : 'medium'}
                              aria-label="Preview listing"
                            >
                              <Language fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: { xs: 1.5, sm: 2 }, 
                          backgroundColor: '#fafafa',
                          maxHeight: { xs: 150, sm: 200 },
                          overflow: 'auto'
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          component="pre" 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            margin: 0,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                          }}
                        >
                          {listing}
                        </Typography>
                      </Paper>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                  <AutoAwesome sx={{ fontSize: { xs: 36, sm: 48 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
                  <Typography 
                    variant="h6" 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    No listings generated yet
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Select a property and unit, then generate your first AI-powered listing!
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, content: '', platform: '' })}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {previewDialog.platform} Listing Preview
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              backgroundColor: '#fafafa',
              maxHeight: { xs: 300, sm: 400 },
              overflow: 'auto'
            }}
          >
            <Typography 
              variant="body2" 
              component="pre" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                margin: 0,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              {previewDialog.content}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => copyToClipboard(previewDialog.content)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Copy
          </Button>
          <Button 
            onClick={() => downloadListing(previewDialog.content, previewDialog.platform.toLowerCase())}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Download
          </Button>
          <Button 
            onClick={() => setPreviewDialog({ open: false, content: '', platform: '' })}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIListingGenerator; 